import type { DefaultOptions, OperationVariables } from '@apollo/client';
import { ApolloClient, ApolloLink, createHttpLink, from, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { map, type Subscription } from 'rxjs';
import type { AbortableRequest } from './FetchUtils';
import FetchUtilsError from '../Errors/FetchUtilsError';
import type { RegisterAbortFunction, AbortFunction } from './AbortUtils';
import { AbortType } from './AbortUtils';

export class ApolloUtilsError extends FetchUtilsError {}

export default class ApolloUtils {
	public static createApolloAuthLink() {
		// create a link that waits and adds the auth headers before sending any requests
		const authContext = setContext(async (_, { headers, ...context }) => {
			// let token = await tokenStore.getTokenData();

			// // No token so we don't add any auth headers
			// if (!token) {
			// 	return {
			// 		...context,
			// 		headers,
			// 	};
			// }

			// // Handle refresh token flow if our token is expired
			// if (isAccessTokenExpired(token)) {
			// 	console.log('Token expired, using refresh token to acquire new access token.');
			// 	try {
			// 		token = await codebotsOauth2Service.getTokenFromRefreshToken(token);
			// 		await tokenStore.setTokenData(token);
			// 	} catch {
			// 		await tokenStore.setTokenData(undefined);
			// 		console.error('Failed to get access token from refresh token, refreshing page');
			// 		refreshTokenFailedCallback();
			// 		return;
			// 	}
			// }

			return {
				...context,
				headers: {
					...headers,
					// authorization: token ? `Bearer ${token.access_token}` : '',
				},
			};
		});

		return authContext;
	}

	public static createApolloErrorHandlerLink() {
		return new ApolloLink((operation, forward) => {
			return forward(operation).pipe(
				map((response) => {
					if (response.errors && response.errors.length) {
						response.errors.forEach(({ message, locations, path }) =>
							console.error(
								`[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
									locations,
								)}, Path: ${path}`,
							),
						);
						throw new ApolloUtilsError(response.errors[0].message, AbortType.UNKNOWN, 0);
					}

					return response;
				}),
			);
		});
	}

	public static createApolloClient(uri: string) {
		const httpLink = createHttpLink({
			uri,
		});

		const authLink = this.createApolloAuthLink();

		const errorHandlerLink = this.createApolloErrorHandlerLink();

		const defaultOptions: DefaultOptions = {
			watchQuery: {
				fetchPolicy: 'no-cache' as never,
				errorPolicy: 'ignore' as never,
			},
			query: {
				fetchPolicy: 'no-cache' as never,
				errorPolicy: 'all' as never,
			},
		};
		return new ApolloClient({
			cache: new InMemoryCache({ resultCaching: false }),
			defaultOptions,
			link: from([errorHandlerLink, authLink, httpLink]),
			// This is what enables cancelling queries
			queryDeduplication: false,
		});
	}

	public static abortableQuery<
		TGqlData = unknown,
		TAccessedData = TGqlData,
		TVariables extends OperationVariables = OperationVariables,
	>(
		apolloClient: ApolloClient,
		options: ApolloClient.WatchQueryOptions<TGqlData, TVariables>,
		dataAccessor?: (data: TGqlData) => TAccessedData,
		registerAbort?: RegisterAbortFunction,
	): AbortableRequest<TAccessedData> {
		const query = apolloClient.watchQuery<TGqlData, TVariables>(options);

		// Observable has to be instantiated before assignment so that the cancel function is available
		// outside of the scope of the promise
		let observable: Subscription;
		let rejectPromise: AbortFunction;

		const abort: AbortFunction = (type = AbortType.EXPLICIT) => {
			// This will implicitly cancel the query if it's still running
			observable.unsubscribe();
			rejectPromise(type);
		};
		const unregsiterAbort = registerAbort?.(abort);

		const dataPromise = new Promise<TAccessedData>((resolve, reject) => {
			observable = query.subscribe({
				next: (result) => {
					// Apollo Client 4 may emit a loading/cache update with no payload before
					// the network response. Wait for data rather than passing `undefined` to
					// callers' accessors (for example, `data => data.programmes`).
					if (result.data === undefined) {
						if (result.error) {
							unregsiterAbort?.();
							reject(result.error);
						}
						return;
					}
					unregsiterAbort?.();
					resolve(dataAccessor ? dataAccessor(result.data as TGqlData) : (result.data as TAccessedData));
				},
				error: reject,
			});

			rejectPromise = (type: AbortType) => reject(new ApolloUtilsError('Aborted', type, 0));
		});

		return {
			response: dataPromise,
			abort,
		} as AbortableRequest<TAccessedData>;
	}
}
